import { EpubNavigator } from '@readium/navigator'
import { Link, Locator, LocatorLocations, Publication, ReadingProgression } from '@readium/shared'
import { ChevronLeft, ChevronRight, Highlighter, Info, List, Search, Settings, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { createHighlight, getEpisodeProgress, updateEpisodeProgress } from '@/api/api'
import { fetchHighlightJumpPosition, Highlight } from '@/api/services/highlight'
import { Footer } from '@/components/shared/footer'
import { Header, type ToolbarItem } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { ActionSection } from '@/components/ui/custom-dropdown'
import { useLatestRef } from '@/hooks/useLatestRef'
import { FOOTER_HEIGHT } from '@/lib/constants'
import { useBookAiStore } from '@/store/useBookAiStore'
import { useProgressStore } from '@/store/useProgressStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { buildAiMarkers } from '../ai/ai-scrollbar-markers'
import {
  buildSpineStarts,
  collectKeySentences,
  epubKeyFromLocator,
  epubLocationFromKey,
  findSpineIndex,
  locatorAtTotalProgression,
  totalProgressionFromKey,
} from '../ai/book-ai-position'
import { BookAiLayer } from '../ai/book-ai-sheet'
import { SelectionActionBar } from '../ai/selection-action-bar'
import { applySkimHighlights, clearSkimHighlights } from '../ai/skim-highlight'
import { TermBadgeBar } from '../ai/term-badge-bar'
import { useBookAi, usePurposePassageKeys, usePurposePassages, useSkimSentences } from '../ai/useBookAi'
import { DEFAULT_READER_SETTINGS, ReaderSettingsDialog } from '../components/reader-settings-dialog'
import { getThemeColors } from '../components/reader-settings-types'
import { HighlightColor } from '../highlight/highlight-colors'
import { HighlightListDialog } from '../highlight/highlight-list-dialog'
import { HighlightSheet } from '../highlight/highlight-sheet'
import { useEpubSettingsQuery } from '../hooks/useEpubSettingsQuery'
import { useHighlightCrud } from '../hooks/useHighlightCrud'
import { useHighlightJumpId } from '../hooks/useHighlightJumpId'
import { useReaderJumpKey } from '../hooks/useReaderJumpKey'
import { CustomScrollbar } from '../shared/custom-scrollbar'
import { SliderColor } from '../shared/slider-color-config'
import { ContinuousEpubViewer, ContinuousViewerHandle, ScrollRatioMeasure } from './components/continuous-epub-viewer'
import { EpubViewer, LocationInfo, TextSelectionInfo } from './components/epub-viewer'
import { SearchDialog } from './components/search-dialog'
import { TocDialog } from './components/toc-dialog'
import type { EpubSearchResult } from './components/toc-utils'
import { searchPublication } from './components/toc-utils'

type Props = {
  episodeId: number
  title: string
  numPages: number
  onInfoClick?: () => void
  onClose: () => void
}

// EPUB Streamerのベース URL
// 本番(HTTPS→HTTPS): VITE_API_URLをそのまま使用
// 開発(HTTPS→HTTP): Viteプロキシ(/api)経由でアクセスし、mixed contentを回避
const viteApiUrl = import.meta.env.VITE_API_URL as string | undefined
const apiBaseUrl = viteApiUrl?.startsWith('https://') ? viteApiUrl : `${window.location.origin}/api`

const EMPTY_SPINE: string[] = []
// 流し読みの強調に使う範囲（今の位置の前後この数の spine にかかる章の重要文だけを使う）
const SKIM_SPINE_MARGIN = 1
// シークバーの目盛り（本全体の進み具合 0〜1 をこの数に分ける）
const SEEK_STEPS = 1000

/** 位置キーを、連続スクロールのスクロール量の比率（0〜1）にする。章がまだ測れなければ null */
const continuousScrollRatio = (measure: ScrollRatioMeasure | null, spine: string[], key: number): number | null => {
  const location = epubLocationFromKey(spine, key)
  if (!measure || !location) return null
  return measure(location.href, location.progression)
}

export const EpubReader = ({ episodeId, title, numPages, onInfoClick, onClose }: Props) => {
  const isFooterPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const [menuVisible, setMenuVisible] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [tocDialogOpen, setTocDialogOpen] = useState(false)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [highlightListOpen, setHighlightListOpen] = useState(false)
  const [highlightSheetOpen, setHighlightSheetOpen] = useState(false)
  const [selectedHighlight, setSelectedHighlight] = useState<Highlight | null>(null)
  const [pendingSelection, setPendingSelection] = useState<TextSelectionInfo | null>(null)
  const [showHighlightButton, setShowHighlightButton] = useState(false)
  const [percentage, setPercentage] = useState(0)
  const [currentLocator, setCurrentLocator] = useState<Locator | null>(null)
  // 表示設定（URL クエリで保持し、リロード・共有時に復元できるようにする）
  const [readerSettings, setReaderSettings] = useEpubSettingsQuery()
  const [toc, setToc] = useState<Link[]>([])
  const [positions, setPositions] = useState<Locator[]>([])
  // 連続スクロールの各章の長さが変わるたびに更新される、位置キー用の測定関数（シークバーの印の位置を引き直す合図）
  const [scrollMeasure, setScrollMeasure] = useState<ScrollRatioMeasure | null>(null)
  const [initialLocator, setInitialLocator] = useState<Locator | null | undefined>(undefined)
  const [isRTL, setIsRTL] = useState(false)
  const [isVertical, setIsVertical] = useState(false)
  const isContinuous = readerSettings.flow === 'continuous'
  const navigatorRef = useRef<EpubNavigator | null>(null)
  const continuousViewerRef = useRef<ContinuousViewerHandle>(null)
  const publicationRef = useRef<Publication | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastLocationRef = useRef<LocationInfo | null>(null)
  const hasCountedPlayRef = useRef(false)
  const [aiSheetOpen, setAiSheetOpen] = useState(false)
  const skimMode = useUISettingsStore((state) => state.readerSkimMode)
  // 流し読みの強調を適用する本文の iframe（表示モードによって複数ある）
  const frameWindowsRef = useRef<Set<Window>>(new Set())

  // AI 読書アシスト: 今読んでいる位置を、解析結果と同じ物差し（spine 番号 + 進み具合）に直す
  const aiSpine =
    useBookAiStore((state) => (state.episodeId === episodeId ? state.status?.spine : undefined)) ?? EMPTY_SPINE
  const spineStarts = useMemo(() => buildSpineStarts(aiSpine, positions), [aiSpine, positions])
  const currentKey = useMemo(
    () => (currentLocator ? epubKeyFromLocator(currentLocator, aiSpine, spineStarts) : null),
    [currentLocator, aiSpine, spineStarts],
  )
  const bookAi = useBookAi(episodeId, currentKey)
  const skimSentences = useSkimSentences(bookAi, SKIM_SPINE_MARGIN)
  const purposeMarks = usePurposePassages(bookAi, Infinity)
  const purposePassages = useMemo(() => purposeMarks.map((p) => p.text), [purposeMarks])
  const purposeKeys = usePurposePassageKeys(episodeId)
  const skimLevel = useUISettingsStore((state) => state.readerSkimLevel)
  // シークバーに、AI の重要文（流し読み中）と読む目的に関係する箇所の印を出す（PDF と同じ配色・強弱）
  // 連続スクロールは、スクロール量と文字数の比率が一致しない（画像など）ので、実際のレイアウトから位置を求める
  const aiMarkers = useMemo(
    () =>
      buildAiMarkers({
        purposeKeys,
        sentences: skimMode ? collectKeySentences(bookAi.sections, skimLevel) : [],
        toValue: (key) => {
          const ratio = isContinuous
            ? continuousScrollRatio(scrollMeasure, aiSpine, key)
            : totalProgressionFromKey(spineStarts, key)
          return ratio == null ? null : Math.round(ratio * SEEK_STEPS)
        },
      }),
    [purposeKeys, skimMode, skimLevel, bookAi.sections, spineStarts, isContinuous, aiSpine, scrollMeasure],
  )
  const skimRef = useLatestRef({ enabled: skimMode, sentences: skimSentences, passages: purposePassages })
  const applySkimToFrame = useCallback(
    (wnd: Window) => {
      const { enabled, sentences, passages } = skimRef.current
      const marks = { sentences: enabled ? sentences : [], passages }
      if (marks.sentences.length > 0 || marks.passages.length > 0) applySkimHighlights(wnd, marks)
      else clearSkimHighlights(wnd)
    },
    [skimRef],
  )

  const handleFrameLoaded = useCallback(
    (wnd: Window) => {
      frameWindowsRef.current.add(wnd)
      applySkimToFrame(wnd)
    },
    [applySkimToFrame],
  )

  // 流し読みの切り替え・解析の進み具合に合わせて、表示中の本文の強調を更新する
  useEffect(() => {
    const frames = frameWindowsRef.current
    for (const wnd of frames) {
      if (wnd.closed) {
        frames.delete(wnd)
        continue
      }
      applySkimToFrame(wnd)
    }
  }, [skimMode, skimSentences, purposePassages, applySkimToFrame])

  // マニフェストURL（バックエンドStreamer経由）
  const manifestUrl = `${apiBaseUrl}/epub/${episodeId}/manifest.json`

  // ハイライトCRUD hook（EPUBでは annotation 操作は不要に — Readiumでは未公開API）
  const {
    highlights,
    setHighlights,
    handleUpdateHighlight: crudUpdate,
    handleDeleteHighlight: crudDelete,
  } = useHighlightCrud({ episodeId })

  // 初期位置を取得（?hl= があればそのハイライトの位置から開く）
  const jumpHighlightId = useHighlightJumpId()
  useEffect(() => {
    const fetchProgress = async () => {
      const jump = await fetchHighlightJumpPosition(jumpHighlightId, episodeId)
      const jumpLocator = jump?.locator ? Locator.deserialize(jump.locator) : undefined
      if (jumpLocator) {
        setInitialLocator(jumpLocator)
        return
      }
      try {
        const progress = await getEpisodeProgress(episodeId)
        if (progress?.locator) {
          // Readium Locator JSON から復元
          const loc = Locator.deserialize(JSON.parse(progress.locator))
          setInitialLocator(loc ?? null)
        } else {
          setInitialLocator(null)
        }
      } catch {
        setInitialLocator(null)
      }
    }
    fetchProgress()
  }, [episodeId, jumpHighlightId])

  // 選択しただけではシートを開かず、操作ボタン（ハイライト・AIに質問）を出す
  const handleTextSelected = useCallback((selection: TextSelectionInfo) => {
    if (!selection.text?.trim()) return
    setPendingSelection(selection)
    setShowHighlightButton(true)
  }, [])

  const handleSelectionCleared = useCallback(() => {
    setShowHighlightButton(false)
    setPendingSelection(null)
  }, [])

  const handleHighlightButtonClick = useCallback(() => {
    setShowHighlightButton(false)
    setHighlightSheetOpen(true)
  }, [])

  const handleSaveHighlight = useCallback(
    async (color: HighlightColor, note: string) => {
      if (!pendingSelection || !episodeId) return

      try {
        const newHighlight = await createHighlight({
          episode_id: episodeId,
          media_type: 'epub',
          position_data: {
            locator: pendingSelection.locator.serialize(),
          },
          text: pendingSelection.text,
          color,
          note: note || undefined,
        })

        setHighlights((prev) => [...prev, newHighlight])
      } catch (error) {
        console.error('ハイライト保存に失敗しました:', error)
      } finally {
        setPendingSelection(null)
        setHighlightSheetOpen(false)
      }
    },
    [pendingSelection, episodeId, setHighlights],
  )

  const handleUpdateHighlight = useCallback(
    async (id: number, color: HighlightColor, note: string) => {
      await crudUpdate(id, color, note)
      setSelectedHighlight(null)
      setHighlightSheetOpen(false)
    },
    [crudUpdate],
  )

  const handleDeleteHighlight = useCallback(
    async (id: number) => {
      await crudDelete(id)
      setSelectedHighlight(null)
      setHighlightSheetOpen(false)
    },
    [crudDelete],
  )

  const saveProgress = useCallback(
    (location: LocationInfo) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      const shouldIncrementPlayCount = location.atEnd && !hasCountedPlayRef.current
      if (shouldIncrementPlayCount) {
        hasCountedPlayRef.current = true
      }
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const currentPage = Math.max(1, Math.round(numPages * location.percentage))
          // 一覧画面（ProductContainer など）の表示を即座に合わせる
          useProgressStore.getState().applyEpisodeProgress(episodeId, currentPage, location.atEnd)
          await updateEpisodeProgress(episodeId, {
            progress: currentPage,
            locator: JSON.stringify(location.locator.serialize()),
            is_completed: location.atEnd,
            increment_play_count: shouldIncrementPlayCount,
          })
        } catch (error) {
          console.error('進捗保存に失敗しました', error)
        }
      }, 2000)
    },
    [episodeId, numPages],
  )

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      if (lastLocationRef.current) {
        const currentPage = Math.max(1, Math.round(numPages * lastLocationRef.current.percentage))
        useProgressStore.getState().applyEpisodeProgress(episodeId, currentPage, lastLocationRef.current.atEnd)
        updateEpisodeProgress(episodeId, {
          progress: currentPage,
          locator: JSON.stringify(lastLocationRef.current.locator.serialize()),
          is_completed: lastLocationRef.current.atEnd,
        }).catch(console.error)
      }
    }
  }, [episodeId, numPages])

  const handleLocationChange = useCallback(
    (location: LocationInfo) => {
      setPercentage(location.percentage)
      setCurrentLocator(location.locator)
      lastLocationRef.current = location
      saveProgress(location)
    },
    [saveProgress],
  )

  const handleClick = useCallback(() => {
    setMenuVisible((prev) => !prev)
  }, [])

  const handleLayoutChange = useCallback((measure: ScrollRatioMeasure) => setScrollMeasure(() => measure), [])

  const handleTocNavigation = useCallback(
    (href: string) => {
      if (isContinuous) {
        continuousViewerRef.current?.goToHref(href)
      } else if (navigatorRef.current) {
        const link = navigatorRef.current.publication.linkWithHref(href)
        if (link) navigatorRef.current.goLink(link, true, () => {})
      }
      setTocDialogOpen(false)
      setMenuVisible(false)
    },
    [isContinuous],
  )

  const handleSearchNavigation = useCallback(
    (result: EpubSearchResult) => {
      if (isContinuous) {
        continuousViewerRef.current?.setActiveSearchHighlight(result)
        continuousViewerRef.current?.goToLocator(result.locator, result.textOffset)
      } else if (navigatorRef.current) {
        navigatorRef.current.go(result.locator, true, () => {})
      }
      setMenuVisible(false)
    },
    [isContinuous],
  )

  /** クライアントサイド検索: 連続スクロールはViewerのメソッド、それ以外はpublicationRefを使用 */
  const handleSearch = useCallback(
    async (query: string): Promise<EpubSearchResult[]> => {
      // 前回のハイライトをクリア
      if (isContinuous) {
        continuousViewerRef.current?.clearSearchHighlights()
      }

      if (isContinuous) {
        const results = await (continuousViewerRef.current?.search(query) ?? Promise.resolve([]))
        if (results.length > 0) {
          continuousViewerRef.current?.highlightSearchResults(query, results)
        }
        return results
      }

      // paginated/scrolledモード: Publication経由で検索
      const pub = publicationRef.current
      if (!pub) return []
      return searchPublication(pub, query)
    },
    [isContinuous],
  )

  /** シークバーの値（0〜SEEK_STEPS）に対応する位置へ移動する */
  const handleSeek = useCallback(
    (value: number) => {
      if (isContinuous) {
        continuousViewerRef.current?.scrollToRatio(value / SEEK_STEPS)
        return
      }
      const target = locatorAtTotalProgression(positions, value / SEEK_STEPS)
      if (!target) return
      navigatorRef.current?.go(target, false, () => {})
    },
    [positions, isContinuous],
  )

  /** AI 読書アシストの位置キー（章の先頭・チャットの根拠）へ移動する */
  const handleAiNavigate = useCallback(
    (key: number) => {
      const target = epubLocationFromKey(aiSpine, key)
      const link = target
        ? publicationRef.current?.readingOrder.items.find((item) => findSpineIndex([target.href], item.href) === 0)
        : undefined
      if (!target || !link) return
      const locator = new Locator({
        href: link.href,
        type: link.mediaType?.string ?? 'application/xhtml+xml',
        locations: new LocatorLocations({ progression: target.progression }),
      })
      if (isContinuous) {
        continuousViewerRef.current?.goToLocator(locator)
      } else {
        navigatorRef.current?.go(locator, true, () => {})
      }
      setMenuVisible(false)
    },
    [aiSpine, isContinuous],
  )

  // ?at= で開いたときは、本と AI の解析結果が揃ってから一度だけその位置へ移動する（本棚への質問・関連の提案から）
  const jumpKey = useReaderJumpKey()
  const jumpedRef = useRef(false)
  useEffect(() => {
    if (jumpKey == null || jumpedRef.current || positions.length === 0 || aiSpine.length === 0) return
    jumpedRef.current = true
    handleAiNavigate(jumpKey)
  }, [jumpKey, positions.length, aiSpine.length, handleAiNavigate])

  const handlePrevChapter = useCallback(() => {
    navigatorRef.current?.goBackward(true, () => {})
  }, [])

  const handleNextChapter = useCallback(() => {
    navigatorRef.current?.goForward(true, () => {})
  }, [])

  const dropdownSections: ActionSection[] = [
    {
      type: 'action',
      label: 'アクション',
      items: [
        ...(toc.length > 0
          ? [
              {
                key: 'toc',
                label: '目次',
                onClick: () => {
                  setTocDialogOpen(true)
                  setMenuVisible(false)
                },
              },
            ]
          : []),
        {
          key: 'reader-settings',
          label: '表示設定',
          onClick: () => {
            setSettingsDialogOpen(true)
            setMenuVisible(false)
          },
        },
        {
          key: 'info',
          label: '詳細情報',
          onClick: () => {
            onInfoClick?.()
          },
        },
      ],
    },
  ]

  const toolbarItems: ToolbarItem[] = [
    {
      icon: <List size={20} />,
      label: '目次',
      onClick: () => setTocDialogOpen(true),
      show: toc.length > 0,
    },
    {
      icon: <Search size={20} />,
      label: '検索',
      onClick: () => setSearchDialogOpen(true),
    },
    {
      icon: <Highlighter size={20} />,
      label: 'ハイライト',
      onClick: () => setHighlightListOpen(true),
    },
    {
      icon: <Sparkles size={20} />,
      label: 'AI',
      onClick: () => {
        setAiSheetOpen(true)
        setMenuVisible(false)
      },
    },
    {
      icon: <Settings size={20} />,
      label: '設定',
      onClick: () => setSettingsDialogOpen(true),
    },
    {
      icon: <Info size={20} />,
      label: '情報',
      onClick: () => onInfoClick?.(),
      show: !!onInfoClick,
    },
  ]

  // 初期位置がまだ読み込み中の場合はローディング表示
  if (initialLocator === undefined) {
    return (
      <div className="fixed w-full h-full flex items-center justify-center bg-[#1a1a1a]">
        <div className="text-gray-400">読み込み中...</div>
      </div>
    )
  }

  const currentPage = Math.max(1, Math.round(numPages * percentage))

  const themeBg = getThemeColors(readerSettings.theme).backgroundColor

  return (
    <div
      className={`w-full h-full fixed scroll-auto`}
      style={{ backgroundColor: themeBg, height: isFooterPinned ? `calc(100% - ${FOOTER_HEIGHT})` : undefined }}
    >
      {isContinuous ? (
        <ContinuousEpubViewer
          ref={continuousViewerRef}
          manifestUrl={manifestUrl}
          initialLocator={initialLocator}
          initialIsVertical={isVertical}
          readerSettings={readerSettings}
          onClick={handleClick}
          onTocLoaded={setToc}
          onPublicationReady={(pub, pos) => {
            publicationRef.current = pub
            const progression = pub.metadata.effectiveReadingProgression
            setIsRTL(progression === ReadingProgression.rtl)
            setPositions(pos)
          }}
          onLocationChange={handleLocationChange}
          onTextSelected={handleTextSelected}
          onSelectionCleared={handleSelectionCleared}
          onWritingModeDetected={setIsVertical}
          onFrameLoaded={handleFrameLoaded}
          onLayoutChange={handleLayoutChange}
        />
      ) : (
        <EpubViewer
          manifestUrl={manifestUrl}
          initialLocator={initialLocator}
          readerSettings={readerSettings}
          onClick={handleClick}
          onTocLoaded={setToc}
          onNavigatorReady={(nav: EpubNavigator, pos: Locator[]) => {
            navigatorRef.current = nav
            publicationRef.current = nav.publication
            const progression = nav.publication.metadata.effectiveReadingProgression
            setIsRTL(progression === ReadingProgression.rtl)
            setPositions(pos)
          }}
          onLocationChange={handleLocationChange}
          onTextSelected={handleTextSelected}
          onSelectionCleared={handleSelectionCleared}
          onWritingModeDetected={setIsVertical}
          onFrameLoaded={handleFrameLoaded}
        />
      )}
      {menuVisible && (
        <>
          <Header title={title} onClose={onClose} dropdownSections={dropdownSections} toolbarItems={toolbarItems} />
          <TermBadgeBar bookAi={bookAi} />

          {!isContinuous && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12"
                onClick={isRTL ? handleNextChapter : handlePrevChapter}
              >
                <ChevronLeft size={28} />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12"
                onClick={isRTL ? handlePrevChapter : handleNextChapter}
              >
                <ChevronRight size={28} />
              </Button>
            </>
          )}
        </>
      )}

      {menuVisible && (isContinuous || positions.length > 0) && (
        <CustomScrollbar
          data-reader-slider
          scrollValue={Math.round(percentage * SEEK_STEPS)}
          onScrollChange={handleSeek}
          max={SEEK_STEPS}
          color={SliderColor.BLUE}
          markers={aiMarkers}
          orientation="horizontal"
          inverted={isRTL}
          className="z-40"
          bottom={FOOTER_HEIGHT}
          left={0}
          right={0}
          height="48px"
        />
      )}

      {(menuVisible || isFooterPinned) && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <Footer
            currentIndex={currentPage}
            totalImages={numPages}
            subtitle={`${Math.round(percentage * 100)}%`}
            pinnable
          />
        </div>
      )}

      <ReaderSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onChange={setReaderSettings}
        settings={readerSettings}
        defaultSettings={DEFAULT_READER_SETTINGS}
        isVertical={isVertical}
      />

      <TocDialog
        open={tocDialogOpen}
        onClose={() => setTocDialogOpen(false)}
        toc={toc}
        positions={positions}
        numPages={numPages}
        currentLocator={currentLocator}
        onNavigate={handleTocNavigation}
      />

      <SearchDialog
        open={searchDialogOpen}
        onClose={() => {
          setSearchDialogOpen(false)
          if (isContinuous) continuousViewerRef.current?.clearSearchHighlights()
        }}
        onSearch={handleSearch}
        onNavigate={handleSearchNavigation}
      />

      <HighlightListDialog
        open={highlightListOpen}
        onClose={() => setHighlightListOpen(false)}
        highlights={highlights}
        mediaType="epub"
        toc={toc}
        positions={positions}
        numPages={numPages}
        onHighlightSelect={(highlight: Highlight) => {
          const locatorData = highlight.position_data.locator
          if (!locatorData) return
          const loc = Locator.deserialize(locatorData)
          if (!loc) return

          if (isContinuous) {
            continuousViewerRef.current?.goToLocator(loc)
          } else if (navigatorRef.current) {
            navigatorRef.current.go(loc, true, () => {})
          }
          setHighlightListOpen(false)
          setMenuVisible(false)
        }}
        onHighlightsChange={setHighlights}
      />

      {showHighlightButton && pendingSelection && (
        <SelectionActionBar
          text={pendingSelection.text}
          bookAi={bookAi}
          onHighlight={handleHighlightButtonClick}
          onAskAi={() => {
            useBookAiStore.getState().setChatQuote(pendingSelection.text)
            handleSelectionCleared()
            setAiSheetOpen(true)
          }}
        />
      )}

      <BookAiLayer
        bookAi={bookAi}
        open={aiSheetOpen}
        onOpenChange={setAiSheetOpen}
        onNavigate={handleAiNavigate}
        suppressChip={menuVisible}
      />

      <HighlightSheet
        open={highlightSheetOpen}
        onClose={() => {
          setHighlightSheetOpen(false)
          setShowHighlightButton(false)
          setPendingSelection(null)
          setSelectedHighlight(null)
        }}
        selectedText={pendingSelection?.text}
        highlight={selectedHighlight}
        onSave={handleSaveHighlight}
        onUpdate={handleUpdateHighlight}
        onDelete={handleDeleteHighlight}
      />
    </div>
  )
}
